/**
 * Recover from a failed MSAL login/redirect so the user can sign in again
 * in one step: clear local auth state, then start a fresh loginRedirect.
 */

export const AUTH_RECOVERY_FLAG = 'pra.auth.recoveryAttempted';

const INTERACTION_KEY_RE = /msal\..*interaction/i;

/**
 * Remove leftover MSAL interaction markers that block a new loginRedirect
 * (common after a cancelled/failed redirect when cacheLocation is localStorage).
 */
export function clearMsalInteractionMarkers(storageLikeList) {
    const storages =
        storageLikeList ||
        (typeof window !== 'undefined' ? [window.localStorage, window.sessionStorage] : []);
    storages.forEach(storage => {
        if (!storage || typeof storage.length !== 'number') return;
        const keysToRemove = [];
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (key && INTERACTION_KEY_RE.test(key)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => storage.removeItem(key));
    });
}

export function hasAuthRecoveryBeenAttempted(sessionStore) {
    const store = sessionStore || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!store) return false;
    try {
        return store.getItem(AUTH_RECOVERY_FLAG) === '1';
    } catch {
        return false;
    }
}

export function markAuthRecoveryAttempted(sessionStore) {
    const store = sessionStore || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!store) return;
    try {
        store.setItem(AUTH_RECOVERY_FLAG, '1');
    } catch {
        // ignore quota / private-mode failures
    }
}

export function clearAuthRecoveryFlag(sessionStore) {
    const store = sessionStore || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!store) return;
    try {
        store.removeItem(AUTH_RECOVERY_FLAG);
    } catch {
        // ignore
    }
}

export function isAuthRelatedError(err) {
    if (!err) return false;
    const code = String(err.errorCode || err.code || '').toLowerCase();
    const name = String(err.name || '').toLowerCase();
    const message = String(err.message || err.errorMessage || '').toLowerCase();
    const haystack = `${code} ${name} ${message}`;
    return (
        haystack.includes('interaction_in_progress') ||
        haystack.includes('login_required') ||
        haystack.includes('interaction_required') ||
        haystack.includes('consent_required') ||
        haystack.includes('auth') ||
        name.includes('auth')
    );
}

/**
 * Local logout (no Azure logout redirect) + fresh loginRedirect.
 * Returns true when a redirect was started; false when recovery was already
 * attempted this tab session (caller should show a manual retry UI).
 */
export async function clearLocalAuthAndLogin(msalInstance, loginRequest, options = {}) {
    const force = options.force === true;
    const sessionStore =
        options.sessionStore || (typeof window !== 'undefined' ? window.sessionStorage : null);

    if (!force && hasAuthRecoveryBeenAttempted(sessionStore)) {
        return false;
    }

    markAuthRecoveryAttempted(sessionStore);

    try {
        if (typeof msalInstance.setActiveAccount === 'function') {
            msalInstance.setActiveAccount(null);
        }
        if (typeof msalInstance.clearCache === 'function') {
            await msalInstance.clearCache();
        }
    } catch (err) {
        console.warn('MSAL clearCache during auth recovery failed:', err);
    }

    clearMsalInteractionMarkers();

    await msalInstance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account',
    });
    return true;
}
